import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useOrgContext } from '../../entities/org';
import { LeaveOrgButton } from '../../features/member/leave-org';
import { DeleteOrgButton } from '../../features/org/delete-org';
import { RenameOrgForm } from '../../features/org/rename-org';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/card';
import { Notice } from '../../shared/ui/notice';
import { SettingsPage } from './settings-page';

/**
 * `/o/:orgSlug/settings/general`: details (renamed with `org.manage`), leaving, and deleting (`org.delete`).
 * Every member may open it, because leaving is here.
 */
export function OrgGeneralPage() {
  const { t } = useTranslation('org');
  const { org, can } = useOrgContext();
  const navigate = useNavigate();
  return (
    <SettingsPage section="general">
      <Card>
        <CardHeader>
          <CardTitle asChild className="font-sans">
            <h2>{t('settings.general.detailsTitle')}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6">
          {can('org.manage') ? (
            <RenameOrgForm org={org} />
          ) : (
            <Notice>{t('settings.general.readOnly')}</Notice>
          )}
          <dl className="grid gap-4 sm:grid-cols-2">
            {!can('org.manage') ? (
              <div className="sm:col-span-2">
                <dt className="text-sm font-bold text-heading">{t('settings.general.name')}</dt>
                <dd>{org.name}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-sm font-bold text-heading">{t('settings.general.country')}</dt>
              <dd>{countryName(org.country)}</dd>
            </div>
            <div>
              <dt className="text-sm font-bold text-heading">{t('settings.general.currency')}</dt>
              <dd>{org.currency}</dd>
            </div>
          </dl>
          <p className="text-sm text-muted-foreground">{t('settings.general.fixedHelp')}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle asChild className="font-sans">
            <h2>{t('settings.leave.title')}</h2>
          </CardTitle>
          <CardDescription>{t('settings.leave.help', { org: org.name })}</CardDescription>
        </CardHeader>
        <CardContent>
          <LeaveOrgButton org={org} onLeft={() => navigate('/')} />
        </CardContent>
      </Card>

      {can('org.delete') ? (
        <Card>
          <CardHeader>
            <CardTitle asChild className="font-sans">
              <h2>{t('settings.delete.title')}</h2>
            </CardTitle>
            <CardDescription>{t('settings.delete.help', { org: org.name })}</CardDescription>
          </CardHeader>
          <CardContent>
            <DeleteOrgButton org={org} onDeleted={() => navigate('/')} />
          </CardContent>
        </Card>
      ) : null}
    </SettingsPage>
  );
}

/** Country name in the viewer's language; the code itself when the runtime does not know it. */
function countryName(code: string): string {
  try {
    return new Intl.DisplayNames([navigator.language], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}
